/**
 * Desktop RECOMMENDATIONS (DESIGN-CHANGELOG 2026-09-03 "RECOMMENDATIONS: the
 * fifth door"; `design/GT Mine.dc.html:183-265`). AA phase 3.
 *
 * Full-screen behind MY GAMES' fifth door: the page bar, the TO YOU / FROM YOU
 * segments, a 920px column. TO YOU is the management view — every send to
 * you, newest first, dismissed ones dimmed with a NOT FOR ME chip, `ADD TO
 * BACKLOG ▸` / `✓ ON YOUR BACKLOG` and `NOT FOR ME` ↔ `BRING IT BACK`. FROM
 * YOU is one row per game you sent, a status chip per recipient, `EDIT ▸`
 * and the ARMED `REVOKE` (`SURE?` in the same cell, the 5 s drain).
 */

import type { GameId } from '../../lib/types.ts';
import { CoverArt } from '../../components/CoverArt.tsx';
import { useArt, useQueueArt } from '../../components/useArt.ts';
import { useQd } from '../../store/index.ts';
import { recsVM } from '../../store/selectors.ts';
import type { RecInRow, RecOutRow } from '../../store/selectors.ts';
import { PageBar } from '../components/PageBar.tsx';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function Av({ ini, hueBg, hasAv, avBg, size }: { ini: string; hueBg: string; hasAv: boolean; avBg: string; size: number }): JSX.Element {
  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        flex: 'none',
        background: hueBg,
        display: 'grid',
        placeItems: 'center',
        fontFamily: PIXEL,
        fontSize: size * 0.3,
        color: 'var(--gt-ink)',
        overflow: 'hidden',
      }}
    >
      {ini}
      {hasAv ? <div style={{ position: 'absolute', inset: 0, background: avBg }} /> : null}
    </div>
  );
}

function Art({ id, ini, fall, cands }: { id: GameId; ini: string; fall: string; cands: RecInRow['hdrCands'] }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const art = useArt(id + 'w', cands);
  return (
    <div
      onClick={() => openGame(id)}
      style={{ position: 'relative', width: 150, height: 70, flex: 'none', background: fall, overflow: 'hidden', border: '1px solid var(--gt-line)', cursor: 'pointer' }}
    >
      <CoverArt style={{ position: 'absolute', inset: 0 }} fs={12} ini={ini} hasCover={art.hasCover} cover={art.cover} />
    </div>
  );
}

function InRow({ r }: { r: RecInRow }): JSX.Element {
  const st = useQd();
  return (
    <div style={{ display: 'flex', gap: 16, padding: '16px 0', borderBottom: '1px solid var(--gt-line-0)', opacity: r.op, transition: 'opacity .12s ease' }}>
      <Art id={r.gid} ini={r.ini} fall={r.fall} cands={r.hdrCands} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Av ini={r.fromIni} hueBg={r.fromHueBg} hasAv={r.fromHasAv} avBg={r.fromAvBg} size={18} />
          <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.07em', color: 'var(--gt-faint)' }}>{r.by}</span>
          {r.dismissed ? (
            <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.09em', color: 'var(--acc2)', border: '1px solid var(--acc2)', padding: '3px 6px' }}>NOT FOR ME</span>
          ) : null}
        </div>
        <span onClick={() => st.openGame(r.gid)} style={{ fontFamily: 'var(--gt-font-sans)', fontWeight: 600, fontSize: 15, color: 'var(--gt-text)', cursor: 'pointer' }}>
          {r.n}
        </span>
        <span style={{ fontFamily: 'var(--gt-font-sans)', fontSize: 14, lineHeight: 1.5, color: 'var(--gt-muted)', textWrap: 'pretty' }}>{r.note}</span>
      </div>
      <div style={{ width: 150, flex: 'none', display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch' }}>
        {r.added ? (
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: 'var(--acc)', padding: '8px 0' }}>✓ ON YOUR BACKLOG</span>
        ) : (
          <span
            onClick={() => void st.sendAdd(r.id)}
            style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: 'var(--acc)', border: '1px solid var(--acc)', background: 'var(--accSoft)', padding: '8px 10px', cursor: 'pointer', textAlign: 'center' }}
          >
            ADD TO BACKLOG ▸
          </span>
        )}
        {!r.added ? (
          <span
            onClick={() => void st.sendSet(r.id, r.dismissed ? '' : 'x')}
            className="d-hov-acc-bd"
            style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: 'var(--gt-text-5)', border: '1px solid var(--gt-border-2)', padding: '8px 10px', cursor: 'pointer', textAlign: 'center' }}
          >
            {r.dismissL}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function OutRow({ r }: { r: RecOutRow }): JSX.Element {
  const st = useQd();
  return (
    <div style={{ display: 'flex', gap: 16, padding: '16px 0', borderBottom: '1px solid var(--gt-line-0)' }}>
      <Art id={r.gid} ini={r.ini} fall={r.fall} cands={r.hdrCands} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
        <span onClick={() => st.openGame(r.gid)} style={{ fontFamily: 'var(--gt-font-sans)', fontWeight: 600, fontSize: 15, color: 'var(--gt-text)', cursor: 'pointer' }}>
          {r.n}
        </span>
        <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.07em', color: 'var(--gt-faint)' }}>{r.by}</span>
        <span style={{ fontFamily: 'var(--gt-font-sans)', fontSize: 14, lineHeight: 1.5, color: 'var(--gt-muted)', textWrap: 'pretty' }}>“{r.note}”</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
          {r.who.map((w) => (
            <div key={w.key} style={{ display: 'flex', alignItems: 'center', gap: 7, border: '1px solid var(--gt-line)', padding: '4px 8px 4px 4px' }}>
              <Av ini={w.ini} hueBg={w.hueBg} hasAv={w.hasAv} avBg={w.avBg} size={18} />
              <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.05em', color: 'var(--gt-text-4)' }}>{w.name}</span>
              <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', color: w.chipClr, border: '1px ' + w.chipBs + ' ' + w.chipClr, padding: '2px 5px' }}>
                {w.chip}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ width: 150, flex: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span
          onClick={() => st.sdOpenEdit(r.gid)}
          style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: 'var(--acc)', border: '1px solid var(--acc)', background: 'var(--accSoft)', padding: '8px 10px', cursor: 'pointer', textAlign: 'center' }}
        >
          EDIT ▸
        </span>
        <span
          onClick={() => (r.armed ? void st.sdRevoke(r.gid) : st.rcArmSet(r.gid))}
          data-arm={'rc' + r.gid}
          data-tip="TAKES IT AWAY FROM EVERYONE YOU SENT IT TO"
          style={{
            display: 'grid',
            fontFamily: MONO,
            fontSize: 9,
            letterSpacing: '.07em',
            color: r.armed ? 'var(--acc2)' : 'var(--gt-text-5)',
            border: '1px solid ' + (r.armed ? 'var(--acc2)' : 'var(--gt-border-2)'),
            padding: '8px 10px',
            cursor: 'pointer',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {r.armed ? (
            <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '100%', background: 'var(--acc2)', opacity: 0.22, animation: 'gt-armdrain 5s linear forwards' }} />
          ) : null}
          <span style={{ gridArea: '1/1', position: 'relative', visibility: r.armed ? 'hidden' : 'visible' }}>REVOKE</span>
          <span style={{ gridArea: '1/1', position: 'relative', visibility: r.armed ? 'visible' : 'hidden' }}>SURE?</span>
        </span>
      </div>
    </div>
  );
}

export default function Recs(): JSX.Element {
  const s = useQd();
  const v = recsVM(s);

  // Same art fetch the phone ledger runs — see the note there.
  useQueueArt([...v.rcIn.map((r) => r.gid), ...v.rcOut.map((r) => r.gid)]);

  return (
    <div data-screen-label="Recommendations" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <PageBar title="RECOMMENDATIONS" />
      <div style={{ width: '100%', maxWidth: 920, boxSizing: 'border-box', margin: '0 auto', padding: '18px 24px 30px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', fontFamily: MONO, fontSize: 10 }}>
          {v.tabs.map((t) => (
            <div
              key={t.key}
              onClick={() => s.setRcTab(t.key)}
              style={{ border: '1px solid ' + t.bd, color: t.clr, background: t.bg, padding: '8px 14px', marginLeft: -1, position: 'relative', zIndex: t.z, cursor: 'pointer' }}
            >
              {t.l}
            </div>
          ))}
        </div>
        <div style={{ borderTop: '1px solid var(--gt-line)' }}>
          {v.tab === 'in' ? v.rcIn.map((r) => <InRow key={r.key} r={r} />) : v.rcOut.map((r) => <OutRow key={r.key} r={r} />)}
          {v.inEmpty ? (
            <div style={{ padding: '30px 0', fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--gt-muted)' }}>NOBODY HAS RECOMMENDED YOU A GAME YET</div>
          ) : null}
          {v.outEmpty ? (
            <div style={{ padding: '30px 0', fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', lineHeight: 2, color: 'var(--gt-muted)' }}>
              YOU HAVEN'T RECOMMENDED ANYTHING YET
              <br />
              RECOMMEND ▸ LIVES ON EVERY GAME'S DETAIL PAGE
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
